/**
 * disposal_companies + disposal_facilities + disposal_facility_aliases +
 * disposal_mqtt_connections + disposal_mqtt_publish_log (DB-07).
 *
 * The disposal-portal schema. Phase 15+ (Operator/Disposal portal) reads/writes
 * these tables; Phase 13 (TANK / MQTT bridge) extends the MQTT publish path.
 * Schema is derived from master spec §16b (DISPOSAL FACILITY PICKER — sort and
 * search informs canonical_name + GIN trigram index), §38 (MQTT TANK GAUGE
 * INTEGRATION — the connection-shape pattern reused for disposal MQTT publish
 * per DEC-010), §49 (DisposalCompany + DisposalFacility schemas), §50
 * (license_number lookup), §84 (MQTT publish for disposal companies — v4.5).
 *
 * Tables (5 total — RLS posture varies):
 *   - disposal_companies: tenant-scoped, RLS-enforced. OWNS the 10 module
 *     boolean columns per DEC-005 (all DEFAULT false; Paelode admin controlled
 *     via Phase 18 admin console connecting as `migrator` role — BYPASSes RLS
 *     intentionally per CONTEXT D-15).
 *   - disposal_facilities: PLATFORM-MANAGED catalog (no tenant_id, no RLS) per
 *     CONTEXT D-16. Paelode owns the canonical list of all known SWD facilities;
 *     every tenant can READ via the implicit GRANT in Plan 02-05's epilogue;
 *     INSERT/UPDATE/DELETE REVOKEd from app_user (only `migrator` mutates).
 *   - disposal_facility_aliases: tenant-scoped via `hauler_tenant_id` (NOT
 *     `tenant_id` — column name nuance). RLS predicate references
 *     `hauler_tenant_id` directly. Implements DEC-007's per-hauler-per-facility
 *     `show_operator_name` boolean (default false; hauler-tenant controlled).
 *   - disposal_mqtt_connections: tenant-scoped, RLS-enforced. Implements DEC-010
 *     connection metadata (topic + publish_trigger + UNIQUE per disposal_company).
 *   - disposal_mqtt_publish_log: tenant-scoped, RLS-enforced. Implements DEC-010
 *     30-day retention shape (publish status + payload snapshot). Phase 23 ships
 *     the BullMQ scheduled cleanup that enforces the 30-day TTL — Phase 2 only
 *     declares the table.
 *
 * Brand-split note: the 10 disposal_companies module booleans are toggled by
 * Paelode admins (Phase 18 admin console). The admin app connects as `migrator`
 * (BYPASSRLS) so it can update modules across tenant boundaries — intentional
 * per CONTEXT D-15/D-16. Tenant-scoped RLS prevents a hauler tenant from
 * elevating their OWN modules; only Paelode admins can.
 *
 * Fuzzy-name search (master spec §16b → RESEARCH #29):
 * disposal_facilities has a GIN(canonical_name) gin_trgm_ops index for fast
 * trigram-based fuzzy search. The pg_trgm extension is REQUIRED — Plan 02-05's
 * hand-edited 0000_init.sql prepends `CREATE EXTENSION IF NOT EXISTS pg_trgm;`
 * BEFORE this index is created. If the extension is missing the migration
 * FAILs at the GIN-index step — fail-loud, not silent-skip (T-02-04-09 mitigation).
 *
 * RLS posture (CONTEXT D-15/D-16):
 *   - disposal_companies: RLS via `tenant_id`.
 *   - disposal_facilities: NO RLS (platform catalog).
 *   - disposal_facility_aliases: RLS via `hauler_tenant_id` (column nuance).
 *   - disposal_mqtt_connections: RLS via `tenant_id`.
 *   - disposal_mqtt_publish_log: RLS via `tenant_id`.
 *   4 pgPolicy calls total in this file.
 *
 * Sources:
 *   - master spec §16b (DISPOSAL FACILITY PICKER — sort + search behavior;
 *     informs canonical_name + GIN index)
 *   - master spec §38 (MQTT TANK GAUGE INTEGRATION — pattern reused for
 *     disposal MQTT publish per DEC-010)
 *   - master spec §49 (DisposalCompany + DisposalFacility schemas — primary)
 *   - master spec §50 (license_number lookup)
 *   - master spec §84 (added v4.5 — MQTT publish for disposal companies)
 *   - gsd/DECISIONS_LOG.md DEC-005 (10 modules, all DEFAULT false)
 *   - gsd/DECISIONS_LOG.md DEC-006 (6 AM facility-local daily-email default —
 *     timezone column on disposal_facilities supports this)
 *   - gsd/DECISIONS_LOG.md DEC-007 (show_operator_name on alias, default false)
 *   - gsd/DECISIONS_LOG.md DEC-010 (MQTT publish: topic, payload, trigger
 *     options, 30-day publish-log retention)
 *   - CONTEXT D-15, D-16, D-18 (RLS, role split, DEC confirmations)
 *   - RESEARCH §DEC-005 lines 245-271 (10 module column list verbatim)
 *   - RESEARCH §audit_events lines 273-321 (sibling RLS-enforced event-log
 *     pattern; mqtt_publish_log uses the same shape)
 *   - RESEARCH Performance Indexes #27-30 (state+county; partial UNIQUE on
 *     license_number; GIN on canonical_name; UNIQUE alias resolution)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - 10 module BOOLEAN columns on disposal_companies, all DEFAULT false:
 *     module_ticket_view / module_daily_email / module_custom_reports /
 *     module_volume_trends / module_capture_rate / module_diversion /
 *     module_acceptance_by_site / module_forward_forecast /
 *     module_data_export / module_mqtt_publish.
 *   - disposal_facilities is platform-managed (NO RLS); INSERT/UPDATE/DELETE
 *     REVOKEd from app_user in Plan 02-05's epilogue.
 *   - disposal_facility_aliases.show_operator_name DEFAULT false; flipped
 *     per (hauler, facility) by hauler-tenant admins.
 *   - disposal_mqtt_connections.publish_trigger CHECK allowlist is exactly
 *     4 values: IMMEDIATELY_ON_DRIVER_COMPLETION / AFTER_INREVIEW_APPROVAL /
 *     ON_INVOICE_GENERATION / MANUAL_ONLY (DEC-010).
 *   - disposal_mqtt_publish_log.publish_status CHECK allowlist is exactly
 *     4 values: PENDING / SUCCESS / FAILED / RETRYING.
 *
 * NEGATIVE NOTE — 30-day retention on disposal_mqtt_publish_log is NOT
 * enforced by Phase 2. The schema declares the table; the retention SWEEP
 * is a Phase 23 BullMQ scheduled job. Do not add an automatic Postgres
 * trigger for TTL — the BullMQ job is the documented mechanism.
 */

import {
  boolean,
  check,
  index,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  text,
  time,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';

/**
 * disposal_companies — disposal-tenant-side row holding the 10 module
 * booleans per DEC-005. Tenant-scoped + RLS — a tenant only sees its own
 * disposal_companies row(s). Paelode admins toggle modules via Phase 18
 * admin console connecting as the `migrator` role (BYPASSRLS) per CONTEXT
 * D-15/D-16.
 *
 * RLS posture:
 *   - tenant-scoped via standard predicate (T-02-04-01 HIGH).
 *   - The 10 module booleans cannot be flipped by app_user against a
 *     different tenant's row — RLS blocks cross-tenant SELECTs and the
 *     same predicate guards UPDATE.
 *
 * Sources:
 *   - master spec §49 (DisposalCompany schema — base columns)
 *   - master spec §84 (MQTT publish for disposal companies — added v4.5)
 *   - DEC-005 (10 portal feature modules, all DEFAULT false)
 *   - DEC-010 (module_mqtt_publish — the 10th module)
 *   - RESEARCH §DEC-005 lines 245-271 (column list verbatim)
 */
export const disposalCompanies = pgTable(
  'disposal_companies',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    legalName: text('legal_name'),
    primaryContactEmail: text('primary_contact_email'),
    // 10 modules per DEC-005 + RESEARCH lines 261-271 (all DEFAULT false; only
    // Paelode admin can flip via the migrator role).
    moduleTicketView: boolean('module_ticket_view').notNull().default(false),
    moduleDailyEmail: boolean('module_daily_email').notNull().default(false),
    moduleCustomReports: boolean('module_custom_reports').notNull().default(false),
    moduleVolumeTrends: boolean('module_volume_trends').notNull().default(false),
    moduleCaptureRate: boolean('module_capture_rate').notNull().default(false),
    moduleDiversion: boolean('module_diversion').notNull().default(false),
    moduleAcceptanceBySite: boolean('module_acceptance_by_site').notNull().default(false),
    moduleForwardForecast: boolean('module_forward_forecast').notNull().default(false),
    moduleDataExport: boolean('module_data_export').notNull().default(false),
    moduleMqttPublish: boolean('module_mqtt_publish').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    // Phase 16 / Task 16.2 (DEC-157) — portal_status wiring per §49.
    // 3-state lifecycle: PAYMENT_PENDING → ACTIVE → SUSPENDED. SWDs have NO
    // free tier — new disposal_companies default to PAYMENT_PENDING at approve
    // time; Paelode flips to ACTIVE via a separate module-activation flow
    // (16.3+) once payment is processed outside the platform. Migration 0064
    // pins the 3-value CHECK; text + CHECK for evolvability (RESEARCH Open
    // Question #1 — pgEnum ALTER is fragile).
    portalStatus: text('portal_status').notNull().default('PAYMENT_PENDING'),
    // Phase 16 / Task 16.2 (DEC-157) — set when Paelode activates the portal
    // (16.3+). NULL until then (registrations approved in 16.2 land in
    // PAYMENT_PENDING with these fields still NULL).
    activatedAt: timestamp('activated_at', { withTimezone: true }),
    activatedByUserId: uuid('activated_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    // Phase 16 / Task 16.4 (DEC-161) — company-level IANA timezone + digest
    // send time. Daily digest recipient path (facility_id IS NULL on the
    // recipient row) fires when this timezone's local hour equals
    // digest_send_time's hour. Both DEFAULTs match DEC-006 (6:00 AM
    // facility-local for individual facility emails).
    timezone: text('timezone').notNull().default('America/Chicago'),
    digestSendTime: time('digest_send_time').notNull().default('06:00:00'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('disposal_companies_tenant_idx').on(t.tenantId),
    // Phase 16 / Task 16.2 (DEC-157) — portal_status filter index for the
    // admin console module-activation queue (16.3+). Migration 0064 pins it.
    index('disposal_companies_portal_status_idx').on(t.portalStatus),
    pgPolicy('disposal_companies_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type DisposalCompany = typeof disposalCompanies.$inferSelect;
export type NewDisposalCompany = typeof disposalCompanies.$inferInsert;
export const disposalCompanySelectSchema = createSelectSchema(disposalCompanies);
export const disposalCompanyInsertSchema = createInsertSchema(disposalCompanies);

/**
 * disposal_facilities — Paelode-managed catalog of all known SWD facilities.
 *
 * NO tenant_id, NO RLS (CONTEXT D-16 platform-managed catalog). Every tenant
 * can READ via the implicit GRANT in Plan 02-05's epilogue; INSERT/UPDATE/DELETE
 * REVOKEd from app_user (only `migrator` can mutate). The Paelode-internal
 * admin app (Phase 18+) connects as `migrator` for catalog-management
 * endpoints.
 *
 * Indexes (RESEARCH #27/#28/#29):
 *   - (state, county) — facility picker by region (master spec §16b).
 *   - PARTIAL UNIQUE (license_number) WHERE license_number IS NOT NULL —
 *     state regulatory permit lookup (master spec §50). Allows multiple
 *     facilities pre-permit (NULL).
 *   - GIN(canonical_name gin_trgm_ops) — fuzzy disposal-picker search
 *     (master spec §16b). REQUIRES the pg_trgm extension; Plan 02-05's
 *     0000_init.sql prepends `CREATE EXTENSION IF NOT EXISTS pg_trgm;`
 *     before the GIN-index CREATE. If the extension is missing, the
 *     migration FAILs loudly (T-02-04-09 mitigation — no silent skip).
 *
 * timezone column supports DEC-006: the daily-email module sends at 6:00 AM
 * facility-local time. Phase 18+'s scheduling job reads this column.
 */
export const disposalFacilities = pgTable(
  'disposal_facilities',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id').references(() => disposalCompanies.id, {
      onDelete: 'restrict',
    }),
    // Master spec §16b — fuzzy search target (lowercase, normalized).
    canonicalName: text('canonical_name').notNull(),
    // Display-friendly name (mixed case, may contain punctuation).
    displayName: text('display_name').notNull(),
    // Master spec §50 — state regulatory permit (OK OCC / TX RRC); UNIQUE WHERE NOT NULL.
    licenseNumber: text('license_number'),
    // 2-letter state code.
    state: varchar('state', { length: 2 }),
    county: text('county'),
    addressLine1: text('address_line_1'),
    addressLine2: text('address_line_2'),
    city: text('city'),
    zipCode: varchar('zip_code', { length: 10 }),
    // IANA timezone for facility-local daily-email default 6:00 AM (DEC-006).
    timezone: text('timezone'),
    // Phase 16 / Task 16.4 (DEC-161) — facility-local send time for the
    // per-facility daily email (site recipients). Digest recipients use the
    // company-level digest_send_time on disposal_companies. Both DEFAULT
    // '06:00:00' per DEC-006. NOT NULL so the sweeper can compare directly.
    dailyEmailSendTime: time('daily_email_send_time').notNull().default('06:00:00'),
    centerGpsLat: numeric('center_gps_lat', { precision: 10, scale: 7 }),
    centerGpsLng: numeric('center_gps_lng', { precision: 10, scale: 7 }),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // RESEARCH #27 — picker by state/county (master spec §16b).
    index('disposal_facilities_state_county_idx').on(t.state, t.county),
    // RESEARCH #28 — license-number lookup (partial UNIQUE; master spec §50).
    uniqueIndex('disposal_facilities_license_number_idx')
      .on(t.licenseNumber)
      .where(sql`license_number IS NOT NULL`),
    // RESEARCH #29 — fuzzy name search via pg_trgm GIN (master spec §16b).
    // The `gin_trgm_ops` operator class requires the pg_trgm extension;
    // Plan 02-05's 0000_init.sql prepends the CREATE EXTENSION call.
    index('disposal_facilities_canonical_name_trgm_idx').using(
      'gin',
      sql`canonical_name gin_trgm_ops`,
    ),
    // No pgPolicy — D-16 platform-managed catalog. Read access via the GRANT
    // in Plan 02-05's epilogue; write access REVOKEd from app_user (only
    // migrator mutates).
  ],
);

export type DisposalFacility = typeof disposalFacilities.$inferSelect;
export type NewDisposalFacility = typeof disposalFacilities.$inferInsert;
export const disposalFacilitySelectSchema = createSelectSchema(disposalFacilities);
export const disposalFacilityInsertSchema = createInsertSchema(disposalFacilities);

/**
 * disposal_facility_aliases — per-hauler-tenant alias resolution + DEC-007
 * `show_operator_name` toggle.
 *
 * RLS-enforced via `hauler_tenant_id` (column-name nuance — NOT generic
 * `tenant_id`). The pgPolicy `using:` predicate references hauler_tenant_id
 * explicitly. Documented for migration auditors and future planners.
 *
 * One alias per (hauler, facility) — RESEARCH #30 partial-UNIQUE-style
 * UNIQUE INDEX. A hauler may rename a facility internally (alias_name) and
 * decide whether the operator name appears on the disposal-portal ticket
 * view (show_operator_name; DEC-007 default false; hauler-tenant controlled).
 *
 * Information-disclosure threat T-02-04-05: when show_operator_name = false,
 * the disposal company NEVER sees the operator name on tickets from this
 * hauler. RLS (cross-hauler SELECTs blocked) plus the column flag (intra-
 * hauler obfuscation) form the two-layer guard.
 */
export const disposalFacilityAliases = pgTable(
  'disposal_facility_aliases',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    haulerTenantId: uuid('hauler_tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    canonicalFacilityId: uuid('canonical_facility_id')
      .notNull()
      .references(() => disposalFacilities.id, { onDelete: 'restrict' }),
    // What THIS hauler calls THIS facility (overrides display_name in their UI).
    aliasName: text('alias_name').notNull(),
    // DEC-007 — hauler-tenant controlled; default false.
    showOperatorName: boolean('show_operator_name').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // RESEARCH #30 — UNIQUE alias per (hauler, facility) pair.
    uniqueIndex('disposal_facility_aliases_hauler_facility_idx').on(
      t.haulerTenantId,
      t.canonicalFacilityId,
    ),
    // RLS — predicate references hauler_tenant_id (NOT tenant_id) per the
    // column-name nuance documented in the module docstring.
    pgPolicy('disposal_facility_aliases_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`hauler_tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type DisposalFacilityAlias = typeof disposalFacilityAliases.$inferSelect;
export type NewDisposalFacilityAlias = typeof disposalFacilityAliases.$inferInsert;
export const disposalFacilityAliasSelectSchema = createSelectSchema(disposalFacilityAliases);
export const disposalFacilityAliasInsertSchema = createInsertSchema(disposalFacilityAliases);

/**
 * MQTT_PUBLISH_TRIGGERS — exactly 4 values pinned verbatim from DEC-010
 * (gsd/DECISIONS_LOG.md). The `disposal_mqtt_connections.publish_trigger`
 * column is locked to this allowlist.
 *
 * Stored as `text` with a CHECK constraint at the column level (NOT pgEnum)
 * for the same evolvability rationale as enums.ts TENANT_USER_ROLES — text+
 * CHECK rewrites are routine; pgEnum ALTER is fragile. Adding a 5th value
 * requires DEC-NNN.
 */
export const MQTT_PUBLISH_TRIGGERS = [
  'IMMEDIATELY_ON_DRIVER_COMPLETION',
  'AFTER_INREVIEW_APPROVAL',
  'ON_INVOICE_GENERATION',
  'MANUAL_ONLY',
] as const;
export type MqttPublishTrigger = (typeof MQTT_PUBLISH_TRIGGERS)[number];

/**
 * MQTT_PUBLISH_STATUSES — 4-value lifecycle for the publish-log row.
 *
 *   PENDING    → row created; publish in flight.
 *   SUCCESS    → broker acknowledged.
 *   FAILED     → broker rejected or transport error after retries.
 *   RETRYING   → transient failure; BullMQ retry scheduled.
 */
export const MQTT_PUBLISH_STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'RETRYING'] as const;
export type MqttPublishStatus = (typeof MQTT_PUBLISH_STATUSES)[number];

/**
 * disposal_mqtt_connections — DEC-010 connection metadata per disposal_company.
 *
 * Tenant-scoped, RLS-enforced. UNIQUE on disposal_company_id (one connection
 * per company; if a company needs multiple topics in the future, the schema
 * needs a column on this table or a new table — DEC-NNN required).
 *
 * Columns (DEC-010):
 *   - topic: MQTT topic, e.g. `lodex/disposal/{disposal_company_id}/tickets`.
 *     Free-text in Phase 2; Phase 13 may add CHECK constraint.
 *   - publish_trigger: when to publish — 4-value CHECK allowlist.
 *
 * Connection broker config (host, port, TLS cert, credentials) is intentionally
 * NOT stored on this table. Phase 13 places those in AWS Secrets Manager
 * (per master spec §38 + master spec §3 secrets-manager pattern).
 */
export const disposalMqttConnections = pgTable(
  'disposal_mqtt_connections',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    disposalCompanyId: uuid('disposal_company_id')
      .notNull()
      .references(() => disposalCompanies.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // DEC-010 — `lodex/disposal/{disposal_company_id}/tickets` shape.
    topic: text('topic').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    // Default 'AFTER_INREVIEW_APPROVAL' per DEC-010.
    publishTrigger: text('publish_trigger').notNull().default('AFTER_INREVIEW_APPROVAL'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // One connection per company (Phase 13 may relax via DEC-NNN).
    uniqueIndex('disposal_mqtt_connections_company_idx').on(t.disposalCompanyId),
    // DEC-010 — 4-value publish_trigger allowlist.
    check(
      'disposal_mqtt_connections_trigger_check',
      sql`publish_trigger IN ('IMMEDIATELY_ON_DRIVER_COMPLETION', 'AFTER_INREVIEW_APPROVAL', 'ON_INVOICE_GENERATION', 'MANUAL_ONLY')`,
    ),
    pgPolicy('disposal_mqtt_connections_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type DisposalMqttConnection = typeof disposalMqttConnections.$inferSelect;
export type NewDisposalMqttConnection = typeof disposalMqttConnections.$inferInsert;
export const disposalMqttConnectionSelectSchema = createSelectSchema(disposalMqttConnections);
export const disposalMqttConnectionInsertSchema = createInsertSchema(disposalMqttConnections);

/**
 * disposal_mqtt_publish_log — DEC-010 30-day publish-log retention shape.
 *
 * Tenant-scoped, RLS-enforced. Append-only at the application layer (Phase 13
 * enforces no-UPDATE except for status transitions PENDING → SUCCESS/FAILED/
 * RETRYING; no DELETE except via Phase 23's BullMQ retention sweep).
 *
 * Phase 23 ships the BullMQ scheduled job that DELETEs rows with
 * `created_at < NOW() - INTERVAL '30 days'`. Phase 2 only declares the
 * shape — there is NO automatic Postgres trigger or partition rule for TTL.
 *
 * Columns (DEC-010 payload + lifecycle):
 *   - payload: JSONB ticket snapshot (haul_id, bbl, operator_name when
 *     show_operator_name = true via DEC-007, etc.).
 *   - publish_status: 4-value lifecycle CHECK allowlist.
 *   - publish_error: human-readable error string when status = FAILED.
 *
 * Index: (tenant_id, created_at DESC) for "recent publish events" dashboard
 * queries from the disposal portal (Phase 15+).
 */
export const disposalMqttPublishLog = pgTable(
  'disposal_mqtt_publish_log',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    connectionId: uuid('connection_id')
      .notNull()
      .references(() => disposalMqttConnections.id, { onDelete: 'cascade' }),
    // 9-digit haul_id per master spec §14 — nullable for non-haul publish events.
    haulId: varchar('haul_id', { length: 9 }),
    // DEC-010 payload shape — JSONB snapshot at publish time.
    payload: jsonb('payload').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    publishStatus: text('publish_status').notNull(),
    // Human-readable error string when status = FAILED.
    publishError: text('publish_error'),
    publishedAt: timestamp('published_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Recent publish-events dashboard (newest first).
    index('disposal_mqtt_publish_log_tenant_created_idx').on(t.tenantId, t.createdAt.desc()),
    // 4-value status allowlist.
    check(
      'disposal_mqtt_publish_log_status_check',
      sql`publish_status IN ('PENDING', 'SUCCESS', 'FAILED', 'RETRYING')`,
    ),
    pgPolicy('disposal_mqtt_publish_log_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type DisposalMqttPublishLog = typeof disposalMqttPublishLog.$inferSelect;
export type NewDisposalMqttPublishLog = typeof disposalMqttPublishLog.$inferInsert;
export const disposalMqttPublishLogSelectSchema = createSelectSchema(disposalMqttPublishLog);
export const disposalMqttPublishLogInsertSchema = createInsertSchema(disposalMqttPublishLog);
